import { useState, useEffect } from 'react'
import { Map } from 'immutable'
import { getViewRecordsAsList } from 'services/data'

// =====================
// Media Utility Functions
// =====================

/**
 * Returns the first available video URL from a mediaUrls object or Map.
 * When options.language is provided, prefers mediaUrls.byLang[language] when present.
 * Otherwise checks common video URL types in order of preference.
 *
 * @param {Object|Map} mediaUrls - The mediaUrls object or Immutable Map (may include byLang)
 * @param {{ language?: string | null }} [options] - Optional; language for byLang lookup
 * @returns {string} The first available video URL, or an empty string if none found
 */
export const getVideoUrl = (mediaUrls, options = {}) => {
  if (!mediaUrls) return ''
  const { language } = options
  if (language) {
    const byLang =
      mediaUrls.get && typeof mediaUrls.get === 'function'
        ? mediaUrls.get('byLang')
        : mediaUrls.byLang
    if (byLang) {
      const langUrl =
        byLang.get && typeof byLang.get === 'function' ? byLang.get(language) : byLang[language]
      if (langUrl) return langUrl
    }
  }
  const urlTypes = ['bcHLS', 'dash', 'bcFLV', 'hls', 'url']
  if (mediaUrls.get && typeof mediaUrls.get === 'function') {
    return urlTypes.map((type) => mediaUrls.get(type)).find(Boolean) || ''
  }
  return urlTypes.map((type) => mediaUrls[type]).find(Boolean) || ''
}

/**
 * Determines if a media record is valid and playable.
 *
 * @param {Map} media - The media record (Immutable Map)
 * @param {string} videoUrl - The resolved video URL
 * @returns {boolean} True if the media is valid and playable, false otherwise
 */
export function isMediaPlayable (media, videoUrl) {
  return !!(media && media.get('mediaId') && videoUrl)
}

// =====================
// useMediaFields Custom Hook
// =====================

/**
 * Custom React hook for managing media field state, autocomplete suggestions, and syncing with Formik.
 * Handles suggestion lists and value synchronization for media fields.
 *
 * @param {Map} data - The Redux data state
 * @param {Array} mediaFields - Array of media field definitions (e.g., [{ name, label }])
 * @param {Function} getDataList - Function to fetch data from the backend
 * @param {string} storeKeyMedia - The media store key
 * @param {Function} onChange - Callback when a media field changes
 * @param {Object} formikValues - The current Formik form values
 * @returns {Object} State and handlers for media fields (inputValues, suggestions, loading, etc.)
 */
export function useMediaFields (
  data,
  mediaFields,
  getDataList,
  storeKeyMedia,
  onChange,
  formikValues,
) {
  // --- State ---
  const [inputValues, setInputValues] = useState({}) // user selection (mediaId)
  const [queryValues, setQueryValues] = useState({}) // debounced value sent to backend
  const [suggestions, setSuggestions] = useState({})
  const [loading, setLoading] = useState({})
  const [touchedFields, setTouchedFields] = useState({})
  const [lastQuerySuggestions, setLastQuerySuggestions] = useState({})

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setQueryValues((prev) => ({ ...prev, [name]: value })) // search string for suggestions
    setLoading((prev) => ({ ...prev, [name]: true }))
    setTouchedFields((prev) => ({ ...prev, [name]: false }))
    setSuggestions((prev) => ({ ...prev, [name]: lastQuerySuggestions[name] || [] }))
    getDataList({ storeKey: storeKeyMedia, processing: true, options: Map({ contentId: value }) })
  }

  const handleSuggestionSelect = (e, record) => {
    const { name } = e.target
    if (record && record.mediaId) {
      setInputValues((prev) => ({
        ...prev,
        [name]: record.mediaId,
      }))
      setQueryValues((prev) => ({ ...prev, [name]: '' }))
      setTouchedFields((prev) => ({ ...prev, [name]: true }))
      setSuggestions((prev) => ({ ...prev, [name]: [] }))
      onChange({ target: { name, value: record.mediaId } }, record)
      getDataList({
        storeKey: storeKeyMedia,
        processing: true,
        options: Map({ mediaId: record.mediaId }),
      })
    } else {
      setSuggestions((prev) => ({ ...prev, [name]: [] }))
    }
  }

  const clearMediaField = (name) => {
    setInputValues((prev) => ({ ...prev, [name]: null }))
    setQueryValues((prev) => ({ ...prev, [name]: '' }))
    setSuggestions((prev) => ({ ...prev, [name]: [] }))
    setLastQuerySuggestions((prev) => ({ ...prev, [name]: [] }))
    setLoading((prev) => ({ ...prev, [name]: false }))
    setTouchedFields((prev) => ({ ...prev, [name]: false }))

    if (onChange) {
      onChange({ target: { name, value: null } })
    }
  }

  // --- Effects ---

  // Fetch autocomplete suggestions for each media field whenever the debounced query value changes.
  useEffect(() => {
    Object.keys(queryValues).forEach((name) => {
      const value = queryValues[name]
      if (value) {
        const records = getViewRecordsAsList({
          data,
          storeKey: storeKeyMedia,
          options: Map({ contentId: value }),
        })
        const backendSuggestions = records
          .map((record) => ({ contentId: record.get('contentId'), mediaId: record.get('mediaId') }))
          .toArray()
        setSuggestions((prev) => ({ ...prev, [name]: backendSuggestions }))
        setLastQuerySuggestions((prev) => ({ ...prev, [name]: backendSuggestions }))
        setLoading((prev) => ({ ...prev, [name]: false }))
      } else {
        setSuggestions((prev) => ({ ...prev, [name]: [] }))
        setLastQuerySuggestions((prev) => ({ ...prev, [name]: [] }))
        setLoading((prev) => ({ ...prev, [name]: false }))
      }
    })
  }, [data, queryValues, storeKeyMedia])

  // Sync local inputValues state with Formik form values.
  useEffect(() => {
    if (!formikValues) return
    const newInputValues = { ...inputValues }
    let changed = false
    mediaFields.forEach((field) => {
      const { name } = field
      const formikValue = formikValues[name]
      if (formikValue) {
        if (inputValues[name] !== formikValue) {
          newInputValues[name] = formikValue
          changed = true
        }
      } else if (inputValues[name] !== null) {
        newInputValues[name] = null
        changed = true
      }
    })
    if (changed) setInputValues(newInputValues)
  }, [formikValues, mediaFields])
  return {
    inputValues,
    suggestions,
    loading,
    touchedFields,
    handleInputChange,
    handleSuggestionSelect,
    clearMediaField,
    setSuggestions,
    setLoading,
    setTouchedFields,
  }
}
